import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, BookOpen, Globe2, CheckCircle2, ArrowRight, Quote,
  GraduationCap, Target, Mic2, Compass
} from "lucide-react";
import { Reveal, SectionLabel, GradientText } from "@/components/ui-bits";
import founderImg from "@/assets/founder.jpg";

export default function AboutSection() {
  return (
    <section id="about" className="cv-container py-20 sm:py-28 relative">
      {/* Subtle Background Glow */}
      <div className="pointer-events-none absolute left-1/4 top-1/2 -translate-y-1/2 h-80 w-80 rounded-full bg-electric/10 blur-[90px]" />

      <Reveal className="mb-14 max-w-3xl">
        <div className="mb-4">
          <SectionLabel color="text-electric">About Us · Founder Story</SectionLabel>
        </div>
        <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
          CVOLVE PRO Was Built Because
          <br />
          <GradientText className="inline-block">I Needed It Myself</GradientText>
        </h2>
        <p className="mt-4 text-lg text-muted-foreground">
          "I am not building CVOLVE PRO while looking at the problem from the outside. I am building it while living the problem."
        </p>
      </Reveal>

      {/* Main Founder Card Grid */}
      <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
        {/* Founder Portrait & Quick Badges */}
        <Reveal className="lg:col-span-5">
          <div className="card-surface relative overflow-hidden p-8 text-center flex flex-col items-center">
            <div className="relative group">
              <div className="absolute -inset-1 rounded-full bg-gradient-to-tr from-electric via-violet to-aqua opacity-75 blur group-hover:opacity-100 transition duration-500" />
              <div className="relative h-44 w-44 sm:h-52 sm:w-52 overflow-hidden rounded-full border-4 border-background shadow-xl">
                <img
                  src={founderImg}
                  alt="Aparajita Sudarshan, Founder of CVOLVE PRO"
                  className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                  onError={(e) => {
                    e.currentTarget.src = "/founder.jpg";
                  }}
                />
              </div>
            </div>

            <h3 className="mt-5 font-heading text-2xl font-bold tracking-tight">Aparajita Sudarshan</h3>
            <p className="text-sm font-semibold text-electric">Founder, CVOLVE PRO</p>
            <p className="text-xs text-muted-foreground mt-1">Author · L&D Specialist · Career Coach</p>

            {/* Country Track */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5">
              {[
                { flag: "🇮🇳", name: "India" },
                { flag: "🇧🇭", name: "Bahrain" },
                { flag: "🇶🇦", name: "Qatar" },
                { flag: "🇲🇾", name: "Malaysia" },
                { flag: "🇦🇪", name: "Dubai" },
                { flag: "🇬🇧", name: "UK" },
              ].map((c, i) => (
                <span
                  key={c.name}
                  className="inline-flex items-center gap-1 rounded-md border border-border/70 bg-secondary/50 px-2 py-0.5 text-xs"
                >
                  <span>{c.flag}</span>
                  <span className="text-[11px] text-muted-foreground font-medium">{c.name}</span>
                </span>
              ))}
            </div>

            {/* Published Books Snippet */}
            <div className="mt-6 w-full rounded-2xl border border-electric/20 bg-electric/5 p-3.5 text-left text-xs">
              <div className="flex items-center gap-1.5 font-bold text-electric mb-1.5">
                <BookOpen className="h-3.5 w-3.5" /> Published Career Author
              </div>
              <p className="text-muted-foreground font-medium">
                • <em>CPC Coding Quick Guide: Essential Exam Review</em><br />
                • <em>From First Job to Leadership: The Ultimate ATS and AI Resume Guide</em>
              </p>
            </div>
          </div>
        </Reveal>

        {/* Narrative & Core Tenets */}
        <Reveal delay={0.15} className="lg:col-span-7 space-y-6">
          <div className="space-y-4 text-base sm:text-lg leading-relaxed text-muted-foreground">
            <p>
              I come from a small town in Uttar Pradesh, India. My career has taken me across <strong className="text-foreground">six countries</strong> with nearly two decades in Learning and Development, building programmes and helping thousands develop their careers.
            </p>
            <p>
              When I moved to the UK as an expatriate, I had the experience, qualifications, and had written the books. But none of that automatically translated into interviews. I discovered that writing a CV isn't a history dump, an ATS score is a diagnostic not a destination, and STAR-based interviewing requires structured preparation.
            </p>
            <p className="font-semibold text-foreground italic border-l-2 border-electric pl-4">
              "I was learning how to become a job seeker while actually being one. That question — why is all of this scattered across so many tools? — became CVOLVE PRO."
            </p>
          </div>

          {/* Key Pillars */}
          <div className="grid gap-3 sm:grid-cols-2 pt-2">
            <div className="rounded-2xl border border-border/70 bg-card p-4">
              <div className="flex items-center gap-2 font-heading font-bold text-sm text-foreground">
                <Target className="h-4 w-4 text-electric" /> Diagnostic, Not Just Score
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                We identify real gaps and ask targeted questions to highlight your genuine experience.
              </p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-card p-4">
              <div className="flex items-center gap-2 font-heading font-bold text-sm text-foreground">
                <Mic2 className="h-4 w-4 text-pink" /> STAR & Face-to-Face Prep
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Prepare for structured STAR questions and realistic interview conversations.
              </p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-card p-4">
              <div className="flex items-center gap-2 font-heading font-bold text-sm text-foreground">
                <Globe2 className="h-4 w-4 text-aqua" /> Multilingual & Global
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Support for English, German, Spanish and French for cross-border opportunities.
              </p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-card p-4">
              <div className="flex items-center gap-2 font-heading font-bold text-sm text-foreground">
                <Compass className="h-4 w-4 text-lime" /> One Job Search. One Place.
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Aggregated jobs, CV customization, ATS checks, and interview copilot all in one.
              </p>
            </div>
          </div>

          {/* Action Links */}
          <div className="pt-2 flex flex-wrap items-center gap-4">
            <Link to="/about" className="cv-btn-primary">
              Read Full Founder Story <ArrowRight className="h-4 w-4" />
            </Link>
            <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-violet" />
              <span>"Career searching should be hard because competition is good, not because the process is broken."</span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
